import FolderSharedIcon from "@mui/icons-material/FolderShared";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useState } from "react";
import { CohortRoles, UserRoles } from "../../../constants";
import { flattenCohorts, flattenTeams } from "../../../stores/jotai/base";
import { SAdminBaseAtom } from "../../../stores/jotai/super-admin/superAdminBase";
import MaterialModal from "../../app/material-modal";
import { useMoveUserRole } from "../../../tanstackQuery/queries/superAdmin/roles";
import { useKeycloak } from "@react-keycloak/web";
import { toast } from "sonner";

interface MoveUserModalProps {
  open: boolean;
  handleClose: () => void;
  data: {
    userName?: string;
    name: string
  }
  source:string;
  currentRoleID:string;
}

export function MoveUserModal({ open, handleClose, data, source,currentRoleID }: MoveUserModalProps) {
  const [sAdminBase, setSAdminBase] = useAtom(SAdminBaseAtom);
  const [selectedGroup, setSelectedGroup] = useState<any>({});
  const [selectedRole, setSelectedRole] = useState("");
   const { keycloak } = useKeycloak();
  const client = useQueryClient();

  const orgTreeData = client.getQueryData([
    "orgtree",
    { orgID: sAdminBase?.selectedOrg?.orgID }
  ]) as any;
  const cohortTeamList = source === "Cohort" ? flattenCohorts(orgTreeData?.hierarchy) :  flattenTeams(orgTreeData?.hierarchy);

  const roles = source === "Cohort" ? CohortRoles : UserRoles;

  const handleSelectGroup = (groupID: string) => {
    const selected = cohortTeamList.find((item) => item.groupID === groupID);
    setSelectedGroup(selected || null);
    setSelectedRole("");
  };

  const {
      mutateAsync: moverUserRole,
      isPending,
      isSuccess,
      isError,
      error:moveUserError
    } = useMoveUserRole(
      { accessToken: keycloak?.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["orgtree"] });
          if(source==="Cohort"){
            client.invalidateQueries({ queryKey: ["orgCohortUsertDetails"] });
          }
          else if(source==="Team"){
            client.invalidateQueries({ queryKey: ["orgCohortTeamUserDetails"] });
          }
          setSelectedGroup({});
          setSelectedRole(""); 
          handleClose();
        },
      }
    );
    
    const handleMoveUser = async () => {
      const newRoleData = {
        roleID: currentRoleID,
        groupID: selectedGroup?.groupID,
        roleName : selectedRole,
      };
      
      try {
        await moverUserRole(newRoleData);
        if (isSuccess) {
          toast.success("New Role successfully added!");
          setSelectedGroup({});
          setSelectedRole(""); 
          handleClose();
        }
      } catch (error) {
        if(isError){
          toast.error(moveUserError?.response?.data?.payload?.message)
        }
        else{
          toast.error(error?.response?.data?.message || "Failed to add new role.");
        }
      }
    };

  return (
    <MaterialModal
      open={open}
      handleDialogClose={handleClose}
      styleOverrides={{
        container: {
          "& .MuiDialog-paper": {
            width: "100%",
            minHeight: "400px",
            maxWidth: "490px",
            ".MuiDialogContent-root": {
              overflow: "auto",
            },
          },
        },
      }}
      title={`Move User to another ${source}`}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {data?.userName && (
          <Stack direction="row" gap={3}>
            <Typography variant="body1" sx={{ flexBasis: "20%" }}>
              User Name
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 700 }}>
              {data?.userName}
            </Typography>
          </Stack>
        )}

        <Stack direction="row" gap={3}>
          <Typography variant="body1" sx={{ flexBasis: "20%" }}>
            Name
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, fontWeight: 700 }}>
            {data?.name}
          </Typography>
        </Stack>
        <Stack direction="row" gap={3}>
          <Typography variant="body1" sx={{ flexBasis: "20%" }}>
            Moving From
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, fontWeight: 700 }}>
            {source}
          </Typography>
        </Stack>
        <Stack direction="row" gap={3}>
          <Typography variant="body1" sx={{ flexBasis: "20%" }}>
            Moving to*
          </Typography>
          <FormControl sx={{ minWidth: 300 }} size="small">
            <Select
              value={selectedGroup?.groupID || ""}
              onChange={(e) => handleSelectGroup(e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>
                <em>Select Destination</em>
              </MenuItem>
              {cohortTeamList.map((item) => (
                <MenuItem key={item.groupID} value={item.groupID}>
                  {item.groupType === "Cohort" ? (
                    <FolderSharedIcon sx={{ marginRight: 1 }} />
                  ) : (
                    <SupervisorAccountIcon sx={{ marginRight: 1 }} />
                  )}
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" gap={3}>
          <Typography variant="body1" sx={{ flexBasis: "20%" }}>
            Role*
          </Typography>
          <FormControl sx={{ minWidth: 300 }} size="small">
            <Select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>
                <em>Select Role</em>
              </MenuItem>
              {Object.entries(roles).map(([roleName, roleValue]) => (
                <MenuItem
                  key={roleValue}
                  value={roleValue}
                  disabled={["Administrator", "SystemAdministrator"].includes(
                    roleValue
                  )}
                >
                  {roleName}
                </MenuItem>
              ))}
            </Select>
            <a href="mailto:"></a>
          </FormControl>
        </Stack>

        <Stack>
          <Stack direction="row" spacing={1} mt={3} justifyContent="end">
            <Button variant="outlined" size="medium" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="medium"
              disabled={isPending || !selectedGroup?.groupID || !selectedRole}
              endIcon={isPending ? <CircularProgress size={16} /> : null}
              onClick={handleMoveUser}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>
    </MaterialModal>
  );
}
